const NavigatedViewer = require('bpmn-js/dist/bpmn-navigated-viewer.production.min.js');
const BaseViewer = require('bpmn-js/dist/bpmn-viewer.production.min.js');
const IimBpmnBreadcrumbs = require('./extension/breadcrumbs/IimBpmnBreadcrumbs');
const XmlParser = require('fast-xml-parser');

class IimBpmnHighlight {

    /**
     * @type {string}
     */
    taskId;

    /**
     * @type {string|number}
     * @private
     */
    _text;


    constructor(taskId, text) {
        this.taskId = taskId;
        this._text = text;
    }

    get count() {
        return this._text;
    }
}

module.exports.IimBpmnHighlight = IimBpmnHighlight;

class IimBpmnViewerConfig {
    /**
     * CSS width of the BPMN viewport. Number values will get interpreted as pixel values.
     * @type {string | number}
     */
    width = '88vw';

    /**
     * CSS height of the BPMN viewport. Number values will get interpreted as pixel values.
     * @type {string | number}
     */
    height = '70vh';

    /**
     * Task highlights appear with this background color. Expects a valid css color value
     * @type {string}
     */
    taskHighlightBgColor = '#336d96';

    /**
     * Task highlights appear with this text color. Expects a valid css color value
     * @type {string}
     */
    taskHighlightTextColor = 'white';

    /**
     * Enables or disables the dragging of the bpmn diagram. Note that the canvas is infinite,
     * and you can therefore 'loose' the diagram by dragging too far.
     * Use the Viewer component method resetScrollAndZoom() to reset the zoom in this case.
     * This option applies only when zoomAndScrollEnabled is true.
     * @type {boolean}
     */
    canvasDraggable = true;


    /**
     * Displays controls for zoom, moving the canvas and viewport reset
     * @type {boolean}
     */
    showControls = true;

    /**
     * Configure the bootstrap button class used by the control panel
     * @type {string}
     */
    controlBtnClass = 'btn btn-secondary';

    /**
     * Enables or disables the zooming and scrolling of the bpmn diagram.
     * If the zoom is enabled it will also be not draggable.
     * @type {boolean}
     */
    zoomAndScrollEnabled = true;
}

module.exports.IimBpmnViewerConfig = IimBpmnViewerConfig;

class BpmnViewer {
    initialized = () => {
    };

    container;

    /**
     * @type {string}
     */
    diagramElementId;

    /**
     * @param type {string}
     * @returns {SVGElement}
     */
    static createSvgElement(type) {
        return document.createElementNS('http://www.w3.org/2000/svg', type);
    }

    _viewer;
    _canvas;
    _config;
    _overlays;
    _activeOverlayHandler;
    parsedBpmn;

    /**
     * @param config {IimBpmnViewerConfig}
     */
    constructor(config) {
        this._config = config;
        const additionalModules = [];
        // disable default breadcrumbs
        additionalModules.push({drilldownBreadcrumbs: ['value', null]});
        additionalModules.push(IimBpmnBreadcrumbs);

        if (!config.canvasDraggable && config.zoomAndScrollEnabled) {
            additionalModules.push({moveCanvas: ['value', null]});
        }
        const options = {additionalModules};

        this._viewer = config.zoomAndScrollEnabled ? new NavigatedViewer(options) : new BaseViewer(options);
        this._viewer.on('import.done', ({error}) => {
            if (!error) {
                this.extractDiagramElementId();
                this._canvas = this._viewer.get('canvas');
                this._overlays = this._viewer.get('overlays');
                this._activeOverlayHandler = new ActiveOverlayHandler(this._overlays);
                this.resetScrollAndZoom();
                this.initialized();
            }
        });
    }

    get canvas() {
        return this._canvas;
    }

    resetScrollAndZoom() {
        this._canvas.zoom('fit-viewport');
    }

    /**
     * Creates a Promise to import the given XML into the current
     * BpmnJS instance, then returns it as an Observable.
     *
     * @see https://github.com/bpmn-io/bpmn-js-callbacks-to-promises#importxml
     *
     * @returns Promise<{ warnings: Array<any> }>
     */
    importDiagram(xml) {
        const parser = new XmlParser.XMLParser({
            allowBooleanAttributes: true,
            ignoreAttributes: false,
            attributeNamePrefix: '',
        });
        this.parsedBpmn = new ParsedBpmn(parser.parse(xml));
        return this._viewer.importXML(xml);
    }

    detach() {
        this._viewer.destroy();
    }

    attachTo(container) {
        this.container = container;
        this._viewer.attachTo(container);

        // remove bpmn.io label
        for (const el of Array.from(container.getElementsByClassName('bjs-powered-by'))) {
            el.remove();
        }
    }

    addParentHighlight(planeElement, count) {
        const parentShape = this.parsedBpmn.findParentShapeFor(planeElement);
        if (parentShape?.planeElement && parentShape.planeId !== this.diagramElementId) {
            this.addHighlight(new IimBpmnHighlight(parentShape.planeElement, count));
            this.addParentHighlight(parentShape.planeElement, count);
        }
    }

    /**
     * @param entry {IimBpmnHighlight}
     */
    highlightTask(entry) {
        this.addHighlight(entry);
        this.addParentHighlight(entry.taskId, entry.count);
    }

    addHighlight(entry) {
        this._activeOverlayHandler.add(entry);
    }

    removeOldHighlights() {
        this._activeOverlayHandler.removeAll();
    }

    extractDiagramElementId() {
        const element = document.querySelector('g[class=layer-root-1]');
        this.diagramElementId = element.getAttribute('data-element-id');
    }
}

module.exports.BpmnViewer = BpmnViewer;


class ActiveOverlayHandler {
    _activeHighlights = new Map();

    constructor(_overlays) {
        this._overlays = _overlays;
    }

    add(highlight) {
        // console.log(highlight);
        if (this._activeHighlights.has(highlight.taskId)) {
            const activeHighlight = this._activeHighlights.get(highlight.taskId);
            activeHighlight.add(highlight.count);
            this.refreshViewFor(activeHighlight);
        } else {
            const highlightId = this.addHighlight(highlight.taskId, IimBpmnHighlightPosition.TOP_RIGHT_CORNER, highlight.count);
            this._activeHighlights.set(
                highlight.taskId,
                new ActiveHighlight(highlight.taskId, highlightId, IimBpmnHighlightPosition.TOP_RIGHT_CORNER, highlight.count)
            );
        }
    }

    removeAll() {
        Array.from(this._activeHighlights.values()).forEach((highlight) => this._overlays.remove(highlight.overlayId));
        this._activeHighlights.clear();
    }

    refreshViewFor(activeHighlight) {
        this._overlays.remove(activeHighlight.overlayId);
        activeHighlight.overlayId = this.addHighlight(
            activeHighlight.taskId,
            activeHighlight.position,
            activeHighlight.count
        );
    }

    addHighlight(taskId, position, count) {
        console.log(taskId, position, count)
        return this._overlays.add(taskId, {
            position: position,
            html: `<div class="diagram-note">${count}</div>`,
        });
    }
}

class ActiveHighlight {
    constructor(
        taskId,
        overlayId,
        position,
        count
    ) {
        this.taskId = taskId;
        this.overlayId = overlayId;
        this.position = position;
        this.count = count;
    }

    add(count) {
        this.count += count;
    }
}

class ParsedBpmn {
    constructor(parsedBpmnXml) {
        this.parsedBpmnXml = parsedBpmnXml;
    }

    findParentShapeFor(taskId) {
        try {
            const diagram = this.parsedBpmnXml['bpmn:definitions']['bpmndi:BPMNDiagram'];
            if (!Array.isArray(diagram)) {
                this.parsedBpmnXml['bpmn:definitions']['bpmndi:BPMNDiagram'] = [diagram];
            }
            for (const diagram of this.parsedBpmnXml['bpmn:definitions'][
                'bpmndi:BPMNDiagram'
                ]) {
                const plane = diagram['bpmndi:BPMNPlane'];
                const shapeList = Array.isArray(plane['bpmndi:BPMNShape'])
                    ? plane['bpmndi:BPMNShape']
                    : [plane['bpmndi:BPMNShape']];
                const shapes = shapeList.map((shape) => new AnnotatedBpmnShape(shape, plane));
                const foundTask = shapes.find((shape) => shape.bpmnElement === taskId);
                if (foundTask) {
                    return foundTask;
                }
            }
        } catch (e) {
            console.error(e);
        }
    }
}

class AnnotatedBpmnShape {
    bpmnElement;
    id;

    get planeElement() {
        return this._plane.bpmnElement;
    }

    get planeId() {
        return this._plane.bpmnElement;
    }

    constructor(shape, _plane) {
        this.bpmnElement = shape.bpmnElement;
        this.id = shape.id;
        this._plane = _plane;
    }
}

class IimBpmnHighlightPosition {
    static get TOP_RIGHT_CORNER() {
        return new IimBpmnHighlightPosition(-5, 15);
    }

    static get TOP_LEFT_CORNER() {
        return new IimBpmnHighlightPosition(-5, undefined, undefined, -5);
    }

    static get BOTTOM_LEFT_CORNER() {
        return new IimBpmnHighlightPosition(undefined, undefined, 15, -5);
    }

    static get BOTTOM_RIGHT_CORNER() {
        return new IimBpmnHighlightPosition(undefined, 15, 15, undefined);
    }

    constructor(
        top,
        right,
        bottom,
        left
    ) {
        this.top = top;
        this.right = right;
        this.bottom = bottom;
        this.left = left;
    }
}

module.exports.IimBpmnHighlightPosition = IimBpmnHighlightPosition;